import { Component, Input, Output, EventEmitter, OnInit, OnChanges, SimpleChanges, ViewChild, ElementRef, HostListener, ChangeDetectorRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

export interface SelectOption {
	value: string | number;
	label: string;
	[key: string]: any; // Allow additional properties
}

@Component({
	selector: 'kt-searchable-select',
	templateUrl: './searchable-select.component.html',
	styleUrls: ['./searchable-select.component.scss'],
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			useExisting: SearchableSelectComponent,
			multi: true
		}
	]
})
export class SearchableSelectComponent implements OnInit, OnChanges, ControlValueAccessor {
	@Input() options: SelectOption[] = [];
	@Input() label: string = '';
	@Input() placeholder: string = 'All';
	@Input() searchPlaceholder: string = 'Search...';
	@Input() disabled: boolean = false;
	@Input() multiple: boolean = true; // Default to true for backward compatibility
	@Input() searchable: boolean = true; // Show search input by default
	@Input() displayValueFn?: (selected: SelectOption[]) => string;
	@Input() clearOnSelect: boolean = false; // If true, clears selection immediately after selecting (for chip-based selections)
	@Input() loading: boolean = false; // If true, shows loading state and prevents opening

	@Output() selectionChange = new EventEmitter<(string | number) | (string | number)[] | null>();
	@Output() immediateSelectionChange = new EventEmitter<(string | number) | (string | number)[] | null>(); // Emits immediately on checkbox change
	@Output() dropdownOpen = new EventEmitter<void>(); // Emits when dropdown opens
	@Output() dropdownClose = new EventEmitter<void>(); // Emits when dropdown closes

	@ViewChild('dropdown', { static: false }) dropdownRef?: ElementRef;
	@ViewChild('searchInput', { static: false }) searchInputRef?: ElementRef;

	isOpen = false;
	searchQuery = '';
	selectedValues: Set<string | number> = new Set();
	filteredOptions: SelectOption[] = [];
	private initialValues: Set<string | number> | null = null; // Track initial state when dropdown opens (for multi-select)

	private onChange = (value: (string | number) | (string | number)[] | null) => {};
	private onTouched = () => {};

	constructor(private cdr: ChangeDetectorRef) {}

	ngOnInit(): void {
		this.filteredOptions = [...this.options];
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes['options']) {
			this.filteredOptions = [...this.options];
			this.filterOptions();
		}
	}

	@HostListener('document:mousedown', ['$event'])
	onDocumentMouseDown(event: MouseEvent): void {
		// Only process if dropdown is open
		if (!this.isOpen) {
			return;
		}

		// Check if click is inside the entire component (including trigger and dropdown)
		const containsTarget = this.dropdownRef && this.dropdownRef.nativeElement.contains(event.target);
		if (containsTarget) {
			// Click is inside component, don't close
			return;
		}

		// Close immediately - mousedown fires before blur, so this should work
		// Use setTimeout with 0ms to allow other mousedown handlers (like option selection) to process first
		setTimeout(() => {
			// Check again if dropdown is still open (might have been closed by option selection)
			if (!this.isOpen) {
				return;
			}

			// Click is outside, close the dropdown
			this.closeDropdown();
		}, 0);
	}


	toggleDropdown(): void {
		if (this.disabled) return;

		if (this.isOpen) {
			this.closeDropdown();
		} else {
			this.openDropdown();
		}
	}

	openDropdown(): void {
		this.isOpen = true;
		this.dropdownOpen.emit(); // Notify parent that dropdown opened
		// In multi-select mode, store initial state to compare when closing
		if (this.multiple) {
			this.initialValues = new Set(this.selectedValues);
		}
		// Focus search input after dropdown opens (only if searchable)
		if (this.searchable) {
			setTimeout(() => {
				if (this.searchInputRef) {
					this.searchInputRef.nativeElement.focus();
				}
			}, 0);
		}
	}

	/**
	 * Public method to close the dropdown programmatically from parent component
	 */
	public closeDropdownProgrammatically(): void {
		if (this.isOpen) {
			this.closeDropdown();
		}
	}

	closeDropdown(): void {
		// In multi-select mode, emit changes when closing if values changed
		if (this.multiple && this.initialValues !== null) {
			// Check if values actually changed from initial state
			const initialValuesStr = Array.from(this.initialValues).sort().join(',');
			const currentValuesStr = Array.from(this.selectedValues).sort().join(',');

			if (initialValuesStr !== currentValuesStr) {
				// Values changed, emit the change
				this.emitChange();
			}
			this.initialValues = null;
		}

		this.isOpen = false;
		this.searchQuery = '';
		this.filterOptions();
		this.onTouched();
		this.cdr.detectChanges(); // Force change detection to ensure dropdown closes visually
		this.dropdownClose.emit(); // Notify parent that dropdown closed
	}

	filterOptions(): void {
		if (!this.searchQuery.trim()) {
			this.filteredOptions = [...this.options];
			return;
		}

		const query = this.searchQuery.toLowerCase();
		this.filteredOptions = this.options.filter(option =>
			option.label.toLowerCase().includes(query)
		);
	}

	isSelected(option: SelectOption): boolean {
		return this.selectedValues.has(option.value);
	}

	onOptionMouseDown(event: MouseEvent, option: SelectOption): void {
		// Prevent default to avoid focus issues
		event.preventDefault();
		event.stopPropagation();
		// Use setTimeout to ensure this runs after any dialog event handlers
		setTimeout(() => {
			this.toggleSelection(option);
		}, 0);
	}

	toggleSelection(option: SelectOption): void {
		if (this.multiple) {
			// Multi-select mode: toggle selection (update UI immediately, but don't emit until dropdown closes)
			if (this.selectedValues.has(option.value)) {
				this.selectedValues.delete(option.value);
			} else {
				this.selectedValues.add(option.value);
			}
			// Emit immediate change event for components that need instant feedback (e.g., chips)
			this.emitImmediateChange();
			// Don't emit selectionChange - will emit when dropdown closes
		} else {
			// Single-select mode: replace selection, emit immediately, and close dropdown
			this.selectedValues.clear();
			this.selectedValues.add(option.value);
			// Emit immediate change for instant feedback
			this.emitImmediateChange();
			// Also emit regular change
			this.emitChange();
			// Clear selection immediately so dropdown shows placeholder (only if clearOnSelect is enabled)
			if (this.clearOnSelect) {
				setTimeout(() => {
					this.selectedValues.clear();
					this.onChange(null);
				}, 0);
			}
			// Close dropdown after selection in single-select mode
			this.closeDropdown();
		}
	}

	selectAll(): void {
		if (!this.multiple) return;

		// Add all filtered options to selection (update UI immediately, but don't emit until dropdown closes)
		this.filteredOptions.forEach(option => {
			this.selectedValues.add(option.value);
		});
	}

	clearSelection(): void {
		if (!this.multiple) {
			this.selectedValues.clear();
			this.emitChange();
			return;
		}

		// Clear selection (update UI immediately, but don't emit until dropdown closes)
		if (this.filteredOptions.length === this.options.length) {
			// Clear all if showing all options
			this.selectedValues.clear();
		} else {
			// Clear only filtered options
			this.filteredOptions.forEach(option => {
				this.selectedValues.delete(option.value);
			});
		}
	}

	isAllSelected(): boolean {
		if (!this.multiple || this.filteredOptions.length === 0) return false;
		return this.filteredOptions.every(option => this.selectedValues.has(option.value));
	}

	isAnySelected(): boolean {
		if (this.filteredOptions.length === 0) return false;
		return this.filteredOptions.some(option => this.selectedValues.has(option.value));
	}

	getDisplayValue(): string {
		if (this.selectedValues.size === 0) {
			return this.placeholder;
		}

		if (this.displayValueFn) {
			const selectedOptions = this.getSelectedOptions();
			return this.displayValueFn(selectedOptions);
		}

		const selectedOptions = this.getSelectedOptions();

		if (!this.multiple) {
			// Single-select: just show the label
			return (selectedOptions.length > 0 && selectedOptions[0].label) || this.placeholder;
		}

		// Multi-select: show count or "All"
		if (selectedOptions.length === 1) {
			return selectedOptions[0].label;
		}

		if (selectedOptions.length === this.options.length) {
			return 'All ' + this.label;
		}

		return `${selectedOptions.length} selected`;
	}

	getSelectedOptions(): SelectOption[] {
		return this.options.filter(option => this.selectedValues.has(option.value));
	}

	private emitChange(): void {
		if (this.multiple) {
			// Multi-select: emit array
			const values = Array.from(this.selectedValues);
			this.onChange(values);
			this.selectionChange.emit(values);
		} else {
			// Single-select: emit single value or null
			const value = this.selectedValues.size > 0 ? Array.from(this.selectedValues)[0] : null;
			this.onChange(value);
			this.selectionChange.emit(value);
		}
	}

	private emitImmediateChange(): void {
		// Emit immediate change for multi-select mode (fires on every checkbox toggle)
		if (this.multiple) {
			const values = Array.from(this.selectedValues);
			this.immediateSelectionChange.emit(values.length > 0 ? values : null);
		} else {
			const value = this.selectedValues.size > 0 ? Array.from(this.selectedValues)[0] : null;
			this.immediateSelectionChange.emit(value);
		}
	}

	// ControlValueAccessor implementation
	writeValue(value: (string | number) | (string | number)[] | null): void {
		if (value === null || value === undefined) {
			this.selectedValues = new Set();
		} else if (Array.isArray(value)) {
			this.selectedValues = new Set(value);
		} else {
			// Single value - set as array if multiple mode, or single value if not
			this.selectedValues = new Set([value]);
		}
		// If dropdown is open in multi-select mode, update initialValues to match
		// so we don't emit a false change when closing
		if (this.isOpen && this.multiple) {
			this.initialValues = new Set(this.selectedValues);
		}
	}

	registerOnChange(fn: (value: (string | number) | (string | number)[] | null) => void): void {
		this.onChange = fn;
	}

	registerOnTouched(fn: () => void): void {
		this.onTouched = fn;
	}

	setDisabledState(isDisabled: boolean): void {
		this.disabled = isDisabled;
	}
}

